import React, { useId, useMemo } from 'react';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { makeSelectExternalMessageSenderIsInternal } from 'flow/selectors/meeting-selectors';
import { TypedMap } from 'flow/types/TypedMap';
import { Message } from 'flow/types/Chats';
import { getMessageSentAriaLabel } from 'flow/utils/message-row-utils';

interface Props {
    message: TypedMap<Message>;
    showAvatar: boolean;
    /** Dark variant, see MessagesPage. */
    videoDark?: boolean;
}

// Render row for a relay message interleaved into the in-meeting chat panel; the sender's actual internal/external label is looked up separately since the relay's own isExternal flag is about routing, not identity (AC-1691).
const ExternalChatMessage = ({
    message,
    showAvatar,
    videoDark = false,
}: Props) => {
    const { i18next } = useAppContext();
    const name = message.getIn(['from', 'name']) ?? '';
    const text = message.get('message');
    const sent = message.get('sent');

    const selectSenderIsInternal = useMemo(
        () => makeSelectExternalMessageSenderIsInternal(),
        [],
    );
    const senderIsInternal = useAppSelector((state) =>
        selectSenderIsInternal(state, name),
    );
    const senderLabel = i18next.t(
        senderIsInternal
            ? 'call.canvas.chat.internal_guest_name'
            : 'call.canvas.chat.external_name',
        { name },
    );
    const bodyId = `${useId()}-body`;
    // The row's name: a colleague sending through the relay is announced by
    // name alone; the guest tag stays a visual hint next to the name.
    const rowName = senderIsInternal ? name : senderLabel;

    return (
        <article
            data-message-row=""
            tabIndex={-1}
            aria-label={`${rowName}, ${getMessageSentAriaLabel(sent)}`}
            aria-describedby={bodyId}
            className={twMerge(
                'group focus-ring-inset relative flex w-full scroll-mt-10 items-start gap-x-2 rounded-sm pe-4',
                showAvatar ? 'ps-1 pt-4' : 'ps-15',
            )}
        >
            {showAvatar && (
                <div className="ms-2 mt-3">
                    <div
                        className={twMerge(
                            'flex size-8 items-center justify-center rounded-full text-xs font-semibold',
                            videoDark
                                ? 'bg-gray-700 text-gray-300'
                                : 'bg-gray-300 text-gray-600',
                        )}
                        aria-label={i18next.t(
                            'call.canvas.chat.external_avatar_label',
                            { name },
                        )}
                    >
                        {name?.charAt(0)?.toUpperCase() ?? '?'}
                    </div>
                </div>
            )}
            <div
                className={twMerge(
                    'min-w-0 flex-1 rounded-sm p-2',
                    videoDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100',
                )}
            >
                {showAvatar && (
                    <div className="flex items-baseline gap-x-2 pb-1">
                        <span
                            className={twMerge(
                                'min-w-0 truncate text-sm font-semibold',
                                videoDark ? 'text-white' : 'text-gray-800',
                            )}
                        >
                            {senderLabel}
                        </span>
                        <span
                            className={twMerge(
                                'shrink-0 text-xs',
                                videoDark ? 'text-gray-400' : 'text-gray-500',
                            )}
                        >
                            {moment(sent).format('HH:mm')}
                        </span>
                    </div>
                )}
                <div
                    id={bodyId}
                    className={twMerge(
                        'text-sm break-words whitespace-pre-wrap',
                        videoDark ? 'text-gray-300' : 'text-gray-800',
                    )}
                >
                    {text}
                </div>
            </div>
        </article>
    );
};

export default ExternalChatMessage;
